/**
 * Auth Helpers — Authentication utilities for the account system
 *
 * Provides:
 * - Password hashing (SHA-256, symbolic/local)
 * - Link code generation & lookup
 * - Session management helpers
 * - Login validation
 *
 * @moved from src/utils/authHelpers.ts (R19 — Fase 2 continuation)
 */

import { AccountsState, Account, isLearnerAccount } from '../types';
import { PASSWORD_MIN_LENGTH, LINK_CODE_MAX_RETRIES, DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_MIN_LENGTH } from '../../config/constants';
import { generateRandomCode } from '../../utils/randomCode';

/**
 * WACHTWOORDEN STAAN HIER NIET MEER
 * ═════════════════════════════════
 *
 * Hier stonden `hashPassword`, `verifyPassword`, `isLegacyPasswordHash` en de
 * bijbehorende SHA-256- en FNV-implementaties (augustus 2026 verwijderd).
 *
 * Ze waren onbereikbaar geworden — Supabase bewaart en verifieert het
 * wachtwoord — maar het echte bezwaar was dat ze bruikbaar bléven ogen. Een
 * ongesalte SHA-256 zonder per-gebruiker salt geeft twee kinderen met
 * hetzelfde wachtwoord dezelfde hash; wie die functie ooit opnieuw zou
 * inschakelen, herintroduceert dat in één import.
 *
 * Er hoort geen tweede plek te zijn waar een wachtwoord verwerkt wordt.
 */

/**
 * Lengte van de koppelcode (ouder ↔ kind).
 *
 * ─── ÉÉN BRON, WANT DRIE SCHERMEN LAZEN HEM ZELF ─────────────────────────────
 *
 * Deze waarde stond als losse `maxLength={6}` in drie invoervelden
 * (`components/settings/SettingsPage`, `parent-v2/pages/HomePage`,
 * `parent-v2/pages/SettingsPage`). Bij een lengtewijziging kapt zo'n veld de
 * code STIL af: de ouder typt acht tekens, ziet er zes, en krijgt "code niet
 * gevonden". Geen foutmelding die naar de oorzaak wijst — precies de faalvorm
 * waar dit project al een paar keer tegenaan liep.
 *
 * ─── WAAROM 8 EN NIET 6 ──────────────────────────────────────────────────────
 *
 * Afgesproken met de backend (aug 2026), samen met de 7-daagse geldigheid. De
 * rekensom die telt is niet "hoe lang duurt het om JOUW code te raden", maar
 * "hoe lang duurt het om ÉÉN geldige code te raden" — een aanvaller raadt tegen
 * alle openstaande codes tegelijk. Met een geldigheid in dagen staan er
 * platformbreed veel codes tegelijk open, en dan is 6 tekens uit 29 (~594
 * miljoen) te krap. 8 tekens brengt dat op ~500 miljard.
 *
 * Kosten voor de gebruiker: twee tekens extra overtypen. Zie
 * `docs/project/SHORT_CODE_SECURITY.md` §7.
 */
export const LINK_CODE_LENGTH = 8;

/**
 * Voorbeeldcode voor placeholders in invoervelden.
 *
 * Staat hier en niet los in de schermen, omdat een placeholder een BELOFTE over
 * de vorm doet. Stond er "Bijv. K7PQR3" boven een veld dat acht tekens
 * verwacht, dan telt een ouder de tekens van zijn eigen code, komt op acht, en
 * twijfelt of hij de goede code heeft. Zo'n mismatch is eerder in dit project
 * voorgekomen en kostte meer support dan de placeholder ooit bespaarde.
 *
 * Bevat uitsluitend tekens uit `UNAMBIGUOUS_CHARSET` en heeft exact
 * `LINK_CODE_LENGTH` tekens — beide bewust, zodat het voorbeeld nooit een code
 * toont die niet kan bestaan.
 */
export const LINK_CODE_EXAMPLE = 'K7PQR3XM';

/**
 * Genereer een koppelcode waarmee een ouder aan een kind gekoppeld wordt.
 * Delegeert naar de gedeelde generator (D-SCAN-2 D2-2).
 */
export function generateLinkCode(): string {
  return generateRandomCode(LINK_CODE_LENGTH);
}

/**
 * Find an account by link code.
 */
export function findAccountByLinkCode(accounts: AccountsState, linkCode: string): Account | null {
  const normalizedCode = linkCode.toUpperCase().trim();
  const allAccounts = Object.values(accounts.accountsById);
  return allAccounts.find(a => isLearnerAccount(a) && a.linkCode === normalizedCode) || null;
}

/**
 * Find an account by username (for learner login).
 */
export function findAccountByUsername(accounts: AccountsState, username: string): Account | null {
  const normalizedUsername = username.toLowerCase().trim();
  const allAccounts = Object.values(accounts.accountsById);
  return allAccounts.find(a => isLearnerAccount(a) && a.username?.toLowerCase() === normalizedUsername) || null;
}

/**
 * Find an account by email (for parent/teacher/admin login).
 */
export function findAccountByEmail(accounts: AccountsState, email: string): Account | null {
  const normalizedEmail = email.toLowerCase().trim();
  const allAccounts = Object.values(accounts.accountsById);
  return allAccounts.find(a => a.email?.toLowerCase() === normalizedEmail) || null;
}

// `isAllDemoAccounts` en `hasProductionAccounts` stonden hier. Vervallen met
// de demo-laag: niets zet `isDemo` nog op `true`, dus beide gaven vanaf dat
// moment een vast antwoord. Een controle die altijd hetzelfde zegt, leest als
// een controle en is er geen.

/**
 * Generate a username suggestion from a display name.
 * E.g., "Djayden van Berg" → "djayden.vanberg"
 */
export function suggestUsername(displayName: string): string {
  return displayName.toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9.]/g, '').replace(/\.{2,}/g, '.').replace(/^\.+|\.+$/g, '');
}

/**
 * Validate a username (3-20 chars, alphanumeric + dots).
 */
export function isValidUsername(username: string): boolean {
  return /^[a-z0-9.]{3,20}$/.test(username);
}

/**
 * Validate a password (min 4 chars for kids, min 6 for adults).
 */
export function isValidPassword(password: string, role: 'learner' | 'parent' | 'teacher' | 'admin' = 'learner'): boolean {
  const minLength = role === 'learner' ? PASSWORD_MIN_LENGTH.learner : PASSWORD_MIN_LENGTH.default;
  return password.length >= minLength;
}

/**
 * VERVALLEN: `isSessionExpired` (augustus 2026 verwijderd).
 *
 * Supabase bewaakt de geldigheid van een sessie en vernieuwt het token
 * automatisch. De lokale variant las `lastActive` uit localStorage en kon
 * daardoor iemand uitloggen met een volstrekt geldig token — halverwege een
 * toets funest.
 */

/**
 * Check if a username is available (not already taken by another account).
 *
 * LET OP: dit kijkt alleen in DEZE browser. Een naam die een ander kind elders
 * al heeft, lijkt hier vrij. De server is de enige die het echt weet, en die
 * weigert met een 409 — die weigering wordt opgevangen in `RegisterScreen`,
 * met drie nieuwe voorstellen. Deze controle is dus een hulpmiddel vooraf,
 * geen waarheid.
 */
export function isUsernameAvailable(accounts: AccountsState, username: string, excludeAccountId?: string): boolean {
  const normalizedUsername = username.toLowerCase().trim();
  if (!normalizedUsername) return false;
  return !Object.values(accounts.accountsById).some(a => a.id !== excludeAccountId && isLearnerAccount(a) && a.username?.toLowerCase() === normalizedUsername);
}

/**
 * Check if an email is available (not already taken by another account).
 */
export function isEmailAvailable(accounts: AccountsState, email: string, excludeAccountId?: string): boolean {
  const normalizedEmail = email.toLowerCase().trim();
  if (!normalizedEmail) return false;
  return !Object.values(accounts.accountsById).some(a => a.id !== excludeAccountId && a.email?.toLowerCase() === normalizedEmail);
}

/**
 * Generate a unique 6-digit link code, checking for collisions.
 * Retries up to LINK_CODE_MAX_RETRIES times to avoid duplicates.
 *
 * @throws Error if no unique code can be generated within the retry budget.
 *         Caller (UI) should surface this as "Kon geen unieke koppelcode
 *         genereren — probeer opnieuw." instead of risking a silent collision.
 */
export function generateUniqueLinkCode(accounts: AccountsState): string {
  for (let attempt = 0; attempt < LINK_CODE_MAX_RETRIES; attempt++) {
    const code = generateLinkCode();
    const existing = findAccountByLinkCode(accounts, code);
    if (!existing) return code;
  }
  throw new Error(`Kon geen unieke koppelcode genereren na ${LINK_CODE_MAX_RETRIES} pogingen. ` + 'Dit kan duiden op een bug of een opvallend volle code-ruimte.');
}

/**
 * Sanitize a display name (trim, collapse whitespace, limit length).
 */
export function sanitizeDisplayName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, DISPLAY_NAME_MAX_LENGTH);
}

/**
 * Sanitize an email address (trim, lowercase).
 */
export function sanitizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Validate registration input comprehensively.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateRegistrationInput(role: 'learner' | 'parent' | 'teacher', input: {
  name: string;
  email?: string;
  password: string;
  schoolName?: string;
}, accounts: AccountsState): string | null {
  // Name validation
  const sanitizedName = sanitizeDisplayName(input.name);
  if (!sanitizedName || sanitizedName.length < DISPLAY_NAME_MIN_LENGTH) {
    return `Naam moet minimaal ${DISPLAY_NAME_MIN_LENGTH} tekens bevatten.`;
  }

  // Password validation
  const minLength = role === 'learner' ? PASSWORD_MIN_LENGTH.learner : PASSWORD_MIN_LENGTH.default;
  if (input.password.length < minLength) {
    return `Wachtwoord moet minimaal ${minLength} tekens bevatten.`;
  }

  // Email validation for non-learner roles
  if (role !== 'learner') {
    if (!input.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
      return 'Vul een geldig e-mailadres in.';
    }
    if (!isEmailAvailable(accounts, input.email)) {
      return 'Dit e-mailadres is al in gebruik. Probeer in te loggen of gebruik een ander adres.';
    }
  }

  // Username uniqueness for learners
  if (role === 'learner') {
    const username = suggestUsername(sanitizedName);
    if (!isUsernameAvailable(accounts, username)) {
      return `De gebruikersnaam "${username}" is al in gebruik. Kies een andere naam.`;
    }
  }

  // School name for teachers
  if (role === 'teacher') {
    // LEEG IS TOEGESTAAN — een bijles- of thuisdocent hoort geen school te
    // verzinnen. Dat was hier een harde eis, waardoor die groep werd gedwongen
    // iets in te vullen; een verzonnen schoolnaam maakt de verificatie die
    // erop volgt waardeloos, want een beheerder kan hem niet natrekken.
    //
    // Wel een ondergrens zodra er íets staat: één teken is een typefout, geen
    // school. Zie TEACHER_VERIFICATION.md §A.3, route 1.
    const school = input.schoolName?.trim() ?? '';
    if (school.length > 0 && school.length < 2) {
      return 'Vul de volledige naam van je school in, of laat het veld leeg.';
    }
  }
  return null;
}